import { Navigate, type RouteObject } from 'react-router-dom';
import { ConnectionSetupPage } from '@/pages/ConnectionSetup';
import { LoginPage } from '@/pages/Login';
import { MainMenuPage } from '@/pages/MainMenu';
import { LoadPage } from '@/pages/Load';
import { UnloadPage } from '@/pages/Unload';
import { PickupDetailPage } from '@/pages/PickupDetail';
import { PickupProgressPage } from '@/pages/PickupProgress';
import { DeliveryDetailPage } from '@/pages/DeliveryDetail';
import { DeliveryProgressPage } from '@/pages/DeliveryProgress';
import { DeadCallPage } from '@/pages/DeadCall';
import { EquipmentPage } from '@/pages/Equipment';
import { PodRecoveryPage } from '@/pages/PodRecovery';
import { StatusModePage } from '@/pages/StatusMode';
import { hasApiUrl } from '@/api/client';
import { useAuthStore } from '@/stores/authStore';
import type { ReactNode } from 'react';

function AuthGuard({ children }: { children: ReactNode }) {
  const isLoggedIn = useAuthStore((s) => s.isLoggedIn);
  if (!hasApiUrl()) return <Navigate to="/setup" replace />;
  if (!isLoggedIn) return <Navigate to="/login" replace />;
  return <>{children}</>;
}

function PublicGuard({ children, requireApiUrl = true }: { children: ReactNode; requireApiUrl?: boolean }) {
  if (requireApiUrl && !hasApiUrl()) return <Navigate to="/setup" replace />;
  return <>{children}</>;
}

export const routes: RouteObject[] = [
  { path: '/setup', element: <ConnectionSetupPage /> },
  {
    path: '/login',
    element: (
      <PublicGuard>
        <LoginPage />
      </PublicGuard>
    ),
  },
  {
    path: '/main',
    element: (
      <AuthGuard>
        <MainMenuPage />
      </AuthGuard>
    ),
  },
  {
    path: '/load',
    element: (
      <AuthGuard>
        <LoadPage />
      </AuthGuard>
    ),
  },
  {
    path: '/load/:id',
    element: (
      <AuthGuard>
        <PickupDetailPage />
      </AuthGuard>
    ),
  },
  {
    path: '/load/:id/progress',
    element: (
      <AuthGuard>
        <PickupProgressPage />
      </AuthGuard>
    ),
  },
  {
    path: '/load/:id/dead-call',
    element: (
      <AuthGuard>
        <DeadCallPage kind="pickup" />
      </AuthGuard>
    ),
  },
  {
    path: '/unload',
    element: (
      <AuthGuard>
        <UnloadPage />
      </AuthGuard>
    ),
  },
  {
    path: '/unload/:id',
    element: (
      <AuthGuard>
        <DeliveryDetailPage />
      </AuthGuard>
    ),
  },
  {
    path: '/unload/:id/progress',
    element: (
      <AuthGuard>
        <DeliveryProgressPage />
      </AuthGuard>
    ),
  },
  {
    path: '/unload/:id/dead-call',
    element: (
      <AuthGuard>
        <DeadCallPage kind="delivery" />
      </AuthGuard>
    ),
  },
  {
    path: '/equipment',
    element: (
      <AuthGuard>
        <EquipmentPage />
      </AuthGuard>
    ),
  },
  {
    path: '/pod-recovery',
    element: (
      <AuthGuard>
        <PodRecoveryPage />
      </AuthGuard>
    ),
  },
  {
    path: '/status/:mode',
    element: (
      <AuthGuard>
        <StatusModePage />
      </AuthGuard>
    ),
  },
  { path: '/', element: <Navigate to="/main" replace /> },
  { path: '*', element: <Navigate to="/main" replace /> },
];
